import React, { useEffect, useRef, useState } from "react"

import { Construction, Home, MapPin, MapPinCheck, TrainFrontTunnel, Warehouse } from "lucide-react"

// Types for the DynamicRoutePlanning component
interface Point {
  row: number
  col: number
}

interface EndPoint extends Point {
  label: string
}

interface StartPoint extends Point {
  label: string
}

interface BlockedPoint extends Point {
  type: "roadblock" | "tunnel"
}

interface DynamicClosure {
  point: Point
  type: "roadblock" | "tunnel"
  delay: number
}

export interface Round {
  name: string
  roundTimeout: number
  startPoint: StartPoint
  endPoints: EndPoint[]
  initialBlockedPoints: BlockedPoint[]
  dynamicClosures: DynamicClosure[]
}

export interface DynamicRoutePlanningProps {
  question: string
  rounds: Round[]
  mapGridSize: number
  onComplete: (score: number, time: number) => void
}

const DynamicRoutePlanning: React.FC<DynamicRoutePlanningProps> = ({
  question,
  rounds,
  mapGridSize,
  onComplete,
}) => {
  // State variables
  const [currentRoundIndex, setCurrentRoundIndex] = useState<number>(-1)
  const [currentRound, setCurrentRound] = useState<Round | null>(null)
  const [isTaskStarted, setIsTaskStarted] = useState<boolean>(false)
  const [score, setScore] = useState<number>(0)
  const [feedback, setFeedback] = useState<string>("")
  const [feedbackType, setFeedbackType] = useState<"success" | "error" | "info">("info")
  const [currentRoutePath, setCurrentRoutePath] = useState<Point[]>([])
  const [housesVisited, setHousesVisited] = useState<boolean[]>([])
  // We're using isRoundActiveRef instead of this state variable, but we need to keep the setter
  const [, setIsRoundActive] = useState<boolean>(false)
  // Track the last clicked cell for highlighting with a border
  const [lastClickedCell, setLastClickedCell] = useState<Point | null>(null)

  // Define cell state type
  type CellState = {
    isStartPoint: boolean
    isEndPoint: boolean
    isBlocked: boolean
    isPath: boolean
    isVisited: boolean
    isError: boolean
    isWarehouseClicked: boolean // Track if warehouse is clicked
    label: string
    blockType: string
  }

  // Grid state
  const [gridState, setGridState] = useState<Array<Array<CellState>>>([])

  // Refs for timers and time tracking
  const startTimeRef = useRef<number>(0)
  const roundTimerRef = useRef<NodeJS.Timeout | null>(null)
  const closureTimersRef = useRef<NodeJS.Timeout[]>([])
  const currentHouseStartTimeRef = useRef<number>(0)
  const totalTimeRef = useRef<number>(0)

  // Refs to track latest state values
  const isTaskStartedRef = useRef<boolean>(false)
  const isRoundActiveRef = useRef<boolean>(false)
  const currentRoundRef = useRef<Round | null>(null)

  // Start the task when the component mounts
  useEffect(() => {
    return () => {
      // Clean up timers when component unmounts
      if (roundTimerRef.current) {
        clearTimeout(roundTimerRef.current)
      }
      closureTimersRef.current.forEach((timer) => clearTimeout(timer))
    }
  }, [])

  // Function to start the task
  const startTask = () => {
    setIsTaskStarted(true)
    isTaskStartedRef.current = true // Update ref
    startTimeRef.current = Date.now()
    startRound(0)
  }

  // Function to start a specific round
  const startRound = (roundIndex: number) => {
    if (roundIndex >= rounds.length) {
      // All rounds completed
      completeTask()
      return
    }

    // Clear any existing timers
    if (roundTimerRef.current) {
      clearTimeout(roundTimerRef.current)
      roundTimerRef.current = null
    }
    closureTimersRef.current.forEach((timer) => clearTimeout(timer))
    closureTimersRef.current = []

    const round = rounds[roundIndex]
    setCurrentRoundIndex(roundIndex)
    setCurrentRound(round)
    currentRoundRef.current = round // Update ref
    setCurrentRoutePath([])
    setHousesVisited(new Array(round.endPoints.length).fill(false))
    setIsRoundActive(true)
    isRoundActiveRef.current = true // Update ref
    currentHouseStartTimeRef.current = Date.now()

    // Set feedback for the start of the round
    setFeedback(
      `Click the WAREHOUSE to begin your route to the first house: ${round.endPoints[0].label}.`,
    )
    setFeedbackType("info")

    // Initialize grid state for this round
    initializeGridState(round)

    // No time limit - only track total time taken

    // Set up dynamic closures for this round
    round.dynamicClosures.forEach((closure) => {
      const timerId = setTimeout(() => {
        triggerRoadClosure(closure)
      }, closure.delay)
      closureTimersRef.current.push(timerId)
    })
  }

  // Function to initialize grid state for the current round
  const initializeGridState = (round: Round) => {
    const newGridState = Array(mapGridSize)
      .fill(null)
      .map(() =>
        Array(mapGridSize)
          .fill(null)
          .map(() => ({
            isStartPoint: false,
            isEndPoint: false,
            isBlocked: false,
            isPath: false,
            isVisited: false,
            isError: false,
            isWarehouseClicked: false,
            label: "",
            blockType: "",
          })),
      )

    // Set start point
    const { row: startRow, col: startCol, label: startLabel } = round.startPoint
    newGridState[startRow][startCol] = {
      ...newGridState[startRow][startCol],
      isStartPoint: true,
      label: startLabel,
    }

    // Set end points (houses)
    round.endPoints.forEach((endPoint) => {
      const { row, col, label } = endPoint
      newGridState[row][col] = {
        ...newGridState[row][col],
        isEndPoint: true,
        label,
      }
    })

    // Set initially blocked points
    round.initialBlockedPoints.forEach((blockedPoint) => {
      const { row, col, type } = blockedPoint
      newGridState[row][col] = {
        ...newGridState[row][col],
        isBlocked: true,
        blockType: type,
      }
    })

    setGridState(newGridState)
  }

  // Function to render a cell based on its state
  const renderCell = (cellState: CellState, row: number, col: number) => {
    let cellClasses =
      "relative flex flex-col items-center justify-center aspect-square rounded transition-all duration-200 ease-in-out cursor-pointer select-none"
    // Use smaller icons on mobile
    let cellIcon = <MapPin className="mb-0 h-4 w-4 text-slate-500 sm:mb-1 sm:h-5 sm:w-5" />

    // Check if this is the last clicked cell to add an outline
    if (lastClickedCell && lastClickedCell.row === row && lastClickedCell.col === col) {
      cellClasses += " outline outline-2 outline-blue-500"
    }

    if (cellState.isStartPoint) {
      // If warehouse is clicked, show yellow background
      if (cellState.isWarehouseClicked) {
        cellClasses += " bg-yellow-400 text-[#0F1D21]"
      } else {
        // Initial warehouse color is green
        cellClasses += " bg-green-600 text-white"
      }
      cellIcon = <Warehouse className="mb-0 h-4 w-4 sm:mb-1 sm:h-5 sm:w-5" />
    } else if (cellState.isEndPoint && cellState.isVisited) {
      // Visited house
      cellClasses += " bg-green-500 text-white"
      cellIcon = <Home className="mb-0 h-4 w-4 text-white sm:mb-1 sm:h-5 sm:w-5" />
    } else if (cellState.isEndPoint) {
      // Houses are blue initially
      cellClasses += " bg-blue-500 text-white"
      cellIcon = <Home className="mb-0 h-4 w-4 text-white sm:mb-1 sm:h-5 sm:w-5" />
    } else if (cellState.isBlocked) {
      // Use custom animation class instead of animate-pulse for slower blinking
      cellClasses += " bg-slate-500 cursor-not-allowed slow-pulse"
      cellIcon =
        cellState.blockType === "tunnel" ? (
          <TrainFrontTunnel className="mb-0 h-4 w-4 text-slate-800 sm:mb-1 sm:h-5 sm:w-5" />
        ) : (
          <Construction className="mb-0 h-4 w-4 text-slate-800 sm:mb-1 sm:h-5 sm:w-5" />
        )
    } else if (cellState.isError) {
      cellClasses += " bg-red-500 text-white"
    } else if (cellState.isPath) {
      // Path cells are yellow
      cellClasses += " bg-yellow-400 text-[#0F1D21]"
      // Use MapPinCheck icon for path cells
      cellIcon = <MapPinCheck className="mb-0 h-4 w-4 text-[#0F1D21] sm:mb-1 sm:h-5 sm:w-5" />
    } else {
      cellClasses += " bg-slate-100 hover:bg-slate-200 hover:scale-105"
    }

    return (
      <div
        key={`${row}-${col}`}
        className={cellClasses}
        data-row={row}
        data-col={col}
        data-block-type={cellState.blockType}
        onClick={() => handleCellClick(row, col, cellState.isBlocked)}
      >
        {cellIcon}
        {cellState.label && (
          <span
            className={`font-semibold ${
              cellState.isStartPoint
                ? "text-[0.45rem] sm:text-[0.6rem]"
                : "text-[0.5rem] sm:text-[0.65rem]"
            }`}
          >
            {cellState.label}
          </span>
        )}
      </div>
    )
  }

  // Function to handle cell clicks
  const handleCellClick = (row: number, col: number, _isBlocked: boolean) => {
    // Check if task has been started - use ref for latest value
    if (!isTaskStartedRef.current) {
      return
    }

    // Use ref for current round instead of state
    if (!currentRoundRef.current) {
      return
    }

    // Check if the cell is already part of the path or visited
    // If it is, do nothing (no penalty, no feedback)
    if (
      gridState[row][col].isPath ||
      gridState[row][col].isVisited ||
      gridState[row][col].isWarehouseClicked
    ) {
      return
    }

    // Special case: Allow clicking on the warehouse to start even if round isn't active yet
    const isWarehouse =
      row === currentRoundRef.current.startPoint.row &&
      col === currentRoundRef.current.startPoint.col

    const clickedPoint: Point = { row, col }

    // Update the last clicked cell for border highlighting
    setLastClickedCell(clickedPoint)

    // First click must be the start point
    if (currentRoutePath.length === 0) {
      if (isWarehouse) {
        // Mark warehouse as clicked
        setGridState((prev) => {
          const newGrid = [...prev]
          newGrid[row][col] = {
            ...newGrid[row][col],
            isWarehouseClicked: true,
          }
          return newGrid
        })

        setCurrentRoutePath([clickedPoint])
        setFeedback("Starting route from warehouse. Click an adjacent cell to continue.")
        setFeedbackType("success")
      } else {
        setScore((prev) => Math.max(0, prev - 0.5))
        setFeedback("Please click the WAREHOUSE to begin.")
        setFeedbackType("error")
        highlightIncorrectClick(row, col)
      }
      return
    }

    // Subsequent clicks must be adjacent to the last point
    const lastPoint = currentRoutePath[currentRoutePath.length - 1]
    if (isAdjacent(lastPoint, clickedPoint)) {
      setCurrentRoutePath((prev) => [...prev, clickedPoint])
      markCellAsPath(row, col)

      // Check if an end point (house) has been reached
      const reachedHouseIndex = currentRoundRef.current.endPoints.findIndex(
        (ep) => ep.row === row && ep.col === col,
      )

      if (reachedHouseIndex !== -1 && !housesVisited[reachedHouseIndex]) {
        // Successfully reached a house!
        const newHousesVisited = [...housesVisited]
        newHousesVisited[reachedHouseIndex] = true
        setHousesVisited(newHousesVisited)

        setScore((prev) => prev + 1)
        markHouseAsVisited(row, col)

        const remainingHouses = currentRoundRef.current.endPoints.filter(
          (_, i) => !newHousesVisited[i],
        )

        if (remainingHouses.length === 0) {
          // All houses in the current round visited
          setFeedback(`All houses in ${currentRoundRef.current.name} visited!`)
          setFeedbackType("success")
          setIsRoundActive(false)
          isRoundActiveRef.current = false // Update ref

          // Move to next round after a delay
          setTimeout(() => {
            startRound(currentRoundIndex + 1)
          }, 1500)
        } else {
          // More houses to visit in this round
          const nextHouseLabel = remainingHouses[0].label
          setFeedback(
            `House ${currentRoundRef.current.endPoints[reachedHouseIndex].label} delivered! Now navigate to ${nextHouseLabel}.`,
          )
          setFeedbackType("success")

          // Reset path for visual clarity, but keep current position
          resetPathVisuals(clickedPoint)
          currentHouseStartTimeRef.current = Date.now()
        }
      } else {
        // Normal movement
        setFeedback("Moving... Continue towards the next house.")
        setFeedbackType("info")
      }
    } else {
      // Not adjacent - incorrect move
      setScore((prev) => Math.max(0, prev - 0.5))
      setFeedback("Incorrect move! Please click an adjacent cell.")
      setFeedbackType("error")
      highlightIncorrectClick(row, col)
    }
  }

  // Helper function to check if two points are adjacent
  const isAdjacent = (p1: Point, p2: Point): boolean => {
    const dRow = Math.abs(p1.row - p2.row)
    const dCol = Math.abs(p1.col - p2.col)
    return (dRow === 1 && dCol === 0) || (dRow === 0 && dCol === 1)
  }

  // Function to trigger a road closure
  const triggerRoadClosure = (closure: DynamicClosure) => {
    const { row, col } = closure.point

    // Check if the cell is already part of the path or visited
    // If it is, don't make it a roadblock
    setGridState((prev) => {
      const newGrid = [...prev]

      // Only apply the closure if the cell is not already part of the path
      // or is not a special cell (start point, end point, etc.)
      if (
        !newGrid[row][col].isPath &&
        !newGrid[row][col].isVisited &&
        !newGrid[row][col].isStartPoint &&
        !newGrid[row][col].isWarehouseClicked
      ) {
        newGrid[row][col] = {
          ...newGrid[row][col],
          isBlocked: true,
          blockType: closure.type,
        }
      }

      return newGrid
    })
  }

  // Function to mark a cell as part of the path
  const markCellAsPath = (row: number, col: number) => {
    setGridState((prev) => {
      const newGrid = [...prev]
      newGrid[row][col] = {
        ...newGrid[row][col],
        isPath: true,
      }
      return newGrid
    })
  }

  // Function to mark a house as visited
  const markHouseAsVisited = (row: number, col: number) => {
    setGridState((prev) => {
      const newGrid = [...prev]
      newGrid[row][col] = {
        ...newGrid[row][col],
        isVisited: true,
      }
      return newGrid
    })
  }

  // Function to highlight an incorrect click
  const highlightIncorrectClick = (row: number, col: number) => {
    setGridState((prev) => {
      const newGrid = [...prev]
      newGrid[row][col] = {
        ...newGrid[row][col],
        isError: true,
      }
      return newGrid
    })

    // Remove the highlight after a short delay
    setTimeout(() => {
      setGridState((prev) => {
        const newGrid = [...prev]
        newGrid[row][col] = {
          ...newGrid[row][col],
          isError: false,
        }
        return newGrid
      })
    }, 800)
  }

  // Function to reset path visuals, keeping only the current position
  const resetPathVisuals = (currentPos: Point) => {
    setGridState((prev) => {
      const newGrid = [...prev]

      // Reset all path cells except for special cells
      for (let r = 0; r < mapGridSize; r++) {
        for (let c = 0; c < mapGridSize; c++) {
          // Skip start points, end points, blocked cells, visited houses, and the current position
          if (
            (r === currentPos.row && c === currentPos.col) ||
            newGrid[r][c].isStartPoint ||
            newGrid[r][c].isEndPoint ||
            newGrid[r][c].isBlocked ||
            newGrid[r][c].isVisited
          ) {
            continue
          }

          // Reset path status for normal cells
          if (newGrid[r][c].isPath) {
            newGrid[r][c] = {
              ...newGrid[r][c],
              isPath: false,
            }
          }
        }
      }

      return newGrid
    })

    setCurrentRoutePath([currentPos])
  }

  // Function to complete the task
  const completeTask = () => {
    // Clean up timers
    if (roundTimerRef.current) {
      clearTimeout(roundTimerRef.current)
      roundTimerRef.current = null
    }
    closureTimersRef.current.forEach((timer) => clearTimeout(timer))
    closureTimersRef.current = []

    // Calculate total time taken
    const timeTaken = (Date.now() - startTimeRef.current) / 1000
    totalTimeRef.current = timeTaken

    // Call the onComplete callback with the final score and time
    onComplete(score, timeTaken)
  }

  const slowPulseStyle = `
    @keyframes slowPulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
    .slow-pulse {
      animation: slowPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
  `

  return (
    <div className="mx-auto max-w-3xl p-2 sm:p-4">
      <style>{slowPulseStyle}</style>
      {!isTaskStarted ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-white p-4 shadow-sm">
            <h3 className="mb-2 text-lg font-semibold">Instructions</h3>
            <p className="text-gray-700">{question}</p>
          </div>
          <div className="text-center">
            <button
              className="rounded-lg bg-[#0F1D21] px-6 py-2 font-bold text-white shadow-md transition-colors duration-300 hover:bg-gray-700"
              onClick={startTask}
            >
              Start Task
            </button>
          </div>
        </div>
      ) : (
        <>
          {currentRound && (
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-lg font-semibold">{currentRound.name}</h3>
              </div>

              <p
                className={`mb-4 rounded-md p-3 ${
                  feedbackType === "success"
                    ? "border border-green-200 bg-green-100 text-green-700"
                    : feedbackType === "error"
                      ? "border border-red-200 bg-red-100 text-red-700"
                      : "border border-blue-200 bg-blue-100 text-blue-700"
                }`}
              >
                {feedback}
              </p>
            </div>
          )}

          <div
            className="mx-auto grid gap-[2px] sm:gap-1"
            style={{
              gridTemplateColumns: `repeat(${mapGridSize}, minmax(30px, 1fr))`,
              maxWidth: "100%",
            }}
          >
            {gridState.map((row, rowIndex) =>
              row.map((cell, colIndex) => renderCell(cell, rowIndex, colIndex)),
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default DynamicRoutePlanning
